import type { StateCreator } from "zustand"
import { useMemo } from "react"
import { create } from "zustand"
import { persist } from "zustand/middleware"

import type {
	ContactsFilterNegations,
	SourceValue,
} from "@productlane/zero/queries"

import { withoutNegation } from "@/components/filters"

export type ContactsFilterNegationKey = keyof ContactsFilterNegations

interface ContactsFilterState {
	negations: ContactsFilterNegations
	setNegation: (key: ContactsFilterNegationKey, isNegated: boolean) => void

	companyIds: Array<string | null>
	addCompany: (companyId: string | null) => void
	removeCompany: (companyId: string | null) => void
	clearCompanies: () => void

	subscribedStates: boolean[]
	addSubscribedState: (value: boolean) => void
	removeSubscribedState: (value: boolean) => void
	clearSubscribedStates: () => void

	sources: SourceValue[]
	addSource: (value: SourceValue) => void
	removeSource: (value: SourceValue) => void
	clearSources: () => void

	reset: () => void
}

const filterDefinition: StateCreator<ContactsFilterState> = (set) => ({
	negations: {},
	setNegation: (key, isNegated) => {
		set((prev) => ({
			negations: isNegated
				? { ...prev.negations, [key]: true }
				: withoutNegation(prev.negations, key),
		}))
	},

	companyIds: [],
	addCompany: (companyId) => {
		set((prev) => ({ companyIds: [...prev.companyIds, companyId] }))
	},
	removeCompany: (companyId) => {
		set((prev) => ({
			companyIds: prev.companyIds.filter((id) => id !== companyId),
		}))
	},
	clearCompanies: () => {
		set((prev) => ({
			companyIds: [],
			negations: withoutNegation(prev.negations, "companyIds"),
		}))
	},

	subscribedStates: [],
	addSubscribedState: (value) => {
		set((prev) => ({ subscribedStates: [...prev.subscribedStates, value] }))
	},
	removeSubscribedState: (value) => {
		set((prev) => ({
			subscribedStates: prev.subscribedStates.filter((v) => v !== value),
		}))
	},
	clearSubscribedStates: () => {
		set((prev) => ({
			subscribedStates: [],
			negations: withoutNegation(prev.negations, "subscribedStates"),
		}))
	},

	sources: [],
	addSource: (value) => {
		set((prev) => ({ sources: [...prev.sources, value] }))
	},
	removeSource: (value) => {
		set((prev) => ({ sources: prev.sources.filter((v) => v !== value) }))
	},
	clearSources: () => {
		set((prev) => ({
			sources: [],
			negations: withoutNegation(prev.negations, "sources"),
		}))
	},

	reset: () => {
		set({ negations: {}, companyIds: [], subscribedStates: [], sources: [] })
	},
})

export const useContactsFilterState = create<ContactsFilterState>()(
	persist(filterDefinition, { name: "contacts-filter-state" }),
)

export function useIsContactsFilterActive() {
	const { companyIds, subscribedStates, sources } = useContactsFilterState()
	return useMemo(
		() =>
			companyIds.length > 0 ||
			subscribedStates.length > 0 ||
			sources.length > 0,
		[companyIds.length, subscribedStates.length, sources.length],
	)
}
